import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  getAccountsStats,
  getProvidersStats,
  getPurchasesStats,
  listJobs,
  listConfig,
} from '../../services/cloneProvider';
import MetricsCharts from './MetricsCharts';
import {
  getErrorMessage,
  formatVnd,
  formatNumber,
  isConfigTrue,
  VerticalStackedBar,
  ProviderStatsByServing,
  LOGIN_STATE_BAR_COLOR,
} from './badges';

const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };
const KIND_LABEL = { recovery: 'Recovery', health_check: 'Health check', buy: 'Buy' };

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-cyan-100 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function JobStatusTable({ jobs, productEvalEnabled }) {
  const serviceTypes = Object.keys(SERVICE_TYPE_LABEL);
  const kinds = Object.keys(KIND_LABEL);

  function findJob(kind, serviceType) {
    return jobs.find((j) => j.kind === kind && j.service_type === serviceType);
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase text-slate-500">
          <th className="py-1"></th>
          {serviceTypes.map((st) => (
            <th key={st} className="py-1 text-center">{SERVICE_TYPE_LABEL[st]}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {kinds.map((kind) => (
          <tr key={kind} className="border-t border-slate-100">
            <td className="py-1.5 font-medium text-slate-700">{KIND_LABEL[kind]}</td>
            {serviceTypes.map((st) => {
              const job = findJob(kind, st);
              return (
                <td key={st} className="py-1.5 text-center">
                  <span
                    title={job?.enabled ? 'Đang bật' : 'Đang tắt'}
                    className={`inline-block h-2.5 w-2.5 rounded-full ${job?.enabled ? 'bg-green-500' : 'bg-red-400'}`}
                  />
                </td>
              );
            })}
          </tr>
        ))}
        <tr className="border-t border-slate-100">
          <td className="py-1.5 font-medium text-slate-700">Product evaluation</td>
          <td colSpan={serviceTypes.length} className="py-1.5 text-center">
            <span
              title={productEvalEnabled ? 'Đang bật' : 'Đang tắt'}
              className={`inline-block h-2.5 w-2.5 rounded-full ${productEvalEnabled ? 'bg-green-500' : 'bg-red-400'}`}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default function OverviewTab({ onNavigate }) {
  const [accountsStats, setAccountsStats] = useState(null);
  const [providersStats, setProvidersStats] = useState(null);
  const [purchasesStats, setPurchasesStats] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [productEvalEnabled, setProductEvalEnabled] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showProviderBars, setShowProviderBars] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [accounts, providers, purchases, jobList, config] = await Promise.all([
        getAccountsStats(),
        getProvidersStats(),
        getPurchasesStats(),
        listJobs(),
        listConfig(),
      ]);
      setAccountsStats(accounts);
      setProvidersStats(providers);
      setPurchasesStats(purchases);
      setJobs(jobList);
      setProductEvalEnabled(isConfigTrue(config.ENABLED_PRODUCT_EVALUATION));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (isLoading && !accountsStats) {
    return <div className="rounded-2xl border border-cyan-100 bg-white p-6 text-center text-sm text-slate-500">Đang tải...</div>;
  }

  const topCheapest = purchasesStats?.product_evaluations?.top_cheapest || [];
  const avgCost =
    purchasesStats && purchasesStats.total_request > 0
      ? purchasesStats.total_vnd / purchasesStats.total_request
      : null;

  const loginStateSegments = accountsStats
    ? Object.entries(accountsStats.by_login_state).map(([state, count]) => ({
        key: state,
        label: state,
        count,
        color: LOGIN_STATE_BAR_COLOR[state] || '#94a3b8',
      }))
    : [];

  return (
    <div className="space-y-4">
      <MetricsCharts />
      
      <div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Tổng Accounts" value={accountsStats?.total ?? '—'} />
          <StatCard label="Tổng Providers" value={providersStats?.total ?? '—'} />
        </div>
      </div>

      <div>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Tổng tiền" value={purchasesStats ? formatVnd(purchasesStats.total_vnd) : '—'} />
          <StatCard label="Tổng request" value={purchasesStats ? formatNumber(purchasesStats.total_request) : '—'} />
          <StatCard label="Avg cost" value={avgCost != null ? (formatVnd(avgCost) + "/request") : '—'} />
        </div>
        
        {purchasesStats?.total_request_by_service_type && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            {Object.entries(purchasesStats.total_request_by_service_type).map(([serviceType, count]) => (
              <StatCard
                key={serviceType}
                label={`Request ${SERVICE_TYPE_LABEL[serviceType] || serviceType}`}
                value={formatNumber(count)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-5">
        <div className="rounded-2xl border border-cyan-100 bg-white p-4 md:col-span-2">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Accounts</h3>
          {accountsStats && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start gap-6">
                <VerticalStackedBar
                  barHeight="h-16"
                  total={accountsStats.total}
                  segments={[
                    { key: 'working', count: accountsStats.by_working_state.working, color: '#22c55e' },
                    { key: 'suspended', count: accountsStats.by_working_state.suspended, color: '#ef4444' },
                  ]}
                />

                <VerticalStackedBar
                  total={accountsStats.total}
                  hideZero
                  barHeight="h-56"
                  segments={loginStateSegments}
                />
              </div>

              {accountsStats.needing_relogin > 0 && (
                <p className="rounded bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700">
                  {accountsStats.needing_relogin} account cần relogin
                </p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-white p-4 md:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Providers theo service_type</h3>
            <label className="flex items-center gap-1.5 text-xs text-slate-500">
              <input type="checkbox" checked={showProviderBars} onChange={(e) => setShowProviderBars(e.target.checked)} />
              Hiện bar
            </label>
          </div>
          {providersStats && (
            <ProviderStatsByServing
              providersStats={providersStats}
              onSegmentClick={(serviceType) => onNavigate && onNavigate('providers', { service_type: serviceType })}
              showBars={showProviderBars}
            />
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-100 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Jobs đang chạy</h3>
        {jobs && <JobStatusTable jobs={jobs} productEvalEnabled={productEvalEnabled} />}
      </div>

      <div className="rounded-2xl border border-cyan-100 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Top 10 rẻ nhất đang theo dõi (còn hàng)</h3>
        {topCheapest.length === 0 ? (
          <p className="text-sm text-slate-500">Chưa có dữ liệu product evaluation.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-1">Est. cost</th>
                <th className="py-1">Tồn kho</th>
                <th className="py-1">ID</th>
                <th className="py-1">Website</th>
                <th className="py-1">Sản phẩm</th>
              </tr>
            </thead>
            <tbody>
              {topCheapest.map((item) => (
                <tr key={`${item.website}-${item.normalized_name}`} className="border-t border-slate-100">
                  <td className="py-1.5">{formatVnd(item.est_cost_vnd)}</td>
                  <td className="py-1.5">{item.in_stock}</td>
                  <td className="py-1.5">{item.external_product_id ?? '—'}</td>
                  <td className="py-1.5">{item.website}</td>
                  <td className="py-1.5">{item.normalized_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button
          type="button"
          onClick={() => onNavigate && onNavigate('evaluations')}
          className="mt-3 text-xs font-medium text-cyan-700 hover:underline"
        >
          Xem đầy đủ ở tab Product Evaluations →
        </button>
      </div>
    </div>
  );
}
